/**
 * ProgressContext - Module progress and assessment tracking for Finding Your Voice
 *
 * Tracks module completion, assessment results, toolkit usage, and scene setter views.
 * Persists to localStorage and, when authenticated, syncs the 'progress'
 * section of the auth hub's per-app thin layer
 * (users/{uid}/apps/finding-your-voice) so account deletion reaches it.
 * The app's own Firestore project is LEGACY: read once per user (one-time
 * import), never written for new state.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, isDemoMode } from '../services/firebase';
import {
  hubSyncAvailable,
  hydrateAppState,
  queuePatch,
  toPlainClient,
} from '../services/hubAppState';
import { useAuth } from './AuthContext';

const ProgressContext = createContext(null);

const STORAGE_KEY = 'fyvProgress';
const PROGRESS_MIGRATED_KEY = 'fyv_hubMigratedProgress';

export const ProgressProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const hasLoadedFromCloud = useRef(false);

  const [moduleProgress, setModuleProgress] = useState({});
  const [assessmentResults, setAssessmentResults] = useState(null);
  const [assessmentCount, setAssessmentCount] = useState(0);
  const [toolkitUsage, setToolkitUsage] = useState([]);
  const [hasSeenSceneSetter, setHasSeenSceneSetter] = useState(false);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [resourcesVisited, setResourcesVisited] = useState(false);

  // Load from localStorage
  const loadLocal = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setModuleProgress(parsed.moduleProgress || {});
        setAssessmentResults(parsed.assessmentResults || null);
        setAssessmentCount(parsed.assessmentCount || (parsed.hasCompletedAssessment ? 1 : 0));
        setToolkitUsage(parsed.toolkitUsage || []);
        setHasSeenSceneSetter(parsed.hasSeenSceneSetter || false);
        setHasCompletedAssessment(parsed.hasCompletedAssessment || false);
        setResourcesVisited(parsed.resourcesVisited || false);
      }
    } catch (err) {
      console.error('Failed to load progress from localStorage:', err);
    }
  }, []);

  // Save to localStorage and optionally Firestore
  const saveData = useCallback(async () => {
    const dataToSave = {
      moduleProgress,
      assessmentResults,
      assessmentCount,
      toolkitUsage,
      hasSeenSceneSetter,
      hasCompletedAssessment,
      resourcesVisited,
    };

    // Always save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (err) {
      console.error('Failed to save progress to localStorage:', err);
    }

    // Queue a debounced, coalesced patch to the hub thin layer when signed in
    if (currentUser?.uid && !isDemoMode && hubSyncAvailable()) {
      queuePatch({ progress: { ...dataToSave, updatedAt: new Date().toISOString() } });
    }
  }, [currentUser, moduleProgress, assessmentResults, assessmentCount, toolkitUsage, hasSeenSceneSetter, hasCompletedAssessment, resourcesVisited]);

  // Hydrate from the hub thin layer (one shared read per login) and merge
  // with local data; one-time import of the legacy own-project doc first.
  const loadFromCloud = useCallback(async (userId) => {
    if (isDemoMode) return;

    // Merge: take the more complete data
    const mergeCloudData = (cloudData) => {
      if (!cloudData) return;

      setModuleProgress(prev => {
        const merged = { ...prev };
        Object.entries(cloudData.moduleProgress || {}).forEach(([moduleId, progress]) => {
          if (!merged[moduleId] || (progress.status === 'completed' && merged[moduleId].status !== 'completed')) {
            merged[moduleId] = progress;
          }
        });
        return merged;
      });

      setToolkitUsage(prev => [...new Set([...prev, ...(cloudData.toolkitUsage || [])])]);

      // Prefer cloud assessment if local doesn't have one
      if (cloudData.hasCompletedAssessment && !hasCompletedAssessment) {
        setHasCompletedAssessment(true);
        setAssessmentResults(cloudData.assessmentResults);
      }

      if (cloudData.assessmentCount) {
        setAssessmentCount(prev => Math.max(prev, cloudData.assessmentCount));
      }

      if (cloudData.hasSeenSceneSetter) {
        setHasSeenSceneSetter(true);
      }

      if (cloudData.resourcesVisited) {
        setResourcesVisited(true);
      }
    };

    try {
      // Legacy first so the hub (the go-forward store) wins any conflicts.
      if (db && userId && !localStorage.getItem(PROGRESS_MIGRATED_KEY)) {
        try {
          const docSnap = await getDoc(doc(db, 'users', userId, 'progress', 'data'));
          if (docSnap.exists()) {
            mergeCloudData(toPlainClient(docSnap.data()));
          }
          localStorage.setItem(PROGRESS_MIGRATED_KEY, '1');
        } catch {
          // no legacy access — retry the import next login
        }
      }

      if (hubSyncAvailable()) {
        const hubState = await hydrateAppState();
        mergeCloudData(hubState?.progress || null);
      }
    } catch (err) {
      console.warn('Cloud load failed, using local data:', err.message);
    }
  }, [hasCompletedAssessment]);

  // Load local data on mount
  useEffect(() => {
    loadLocal();
  }, [loadLocal]);

  // Load from cloud when user signs in
  useEffect(() => {
    if (currentUser?.uid && !hasLoadedFromCloud.current) {
      hasLoadedFromCloud.current = true;
      loadFromCloud(currentUser.uid);
    } else if (!currentUser) {
      hasLoadedFromCloud.current = false;
    }
  }, [currentUser, loadFromCloud]);

  // Auto-save when data changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      saveData();
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, [moduleProgress, assessmentResults, assessmentCount, toolkitUsage, hasSeenSceneSetter, hasCompletedAssessment, resourcesVisited]);

  /**
   * Mark a module as started
   */
  const markModuleStarted = useCallback((moduleId) => {
    setModuleProgress(prev => {
      if (prev[moduleId]?.status === 'completed') return prev;
      return {
        ...prev,
        [moduleId]: {
          ...(prev[moduleId] || {}),
          status: 'started',
          startedAt: prev[moduleId]?.startedAt || new Date().toISOString(),
        },
      };
    });
  }, []);

  /**
   * Mark a module as completed
   */
  const markModuleCompleted = useCallback((moduleId) => {
    setModuleProgress(prev => ({
      ...prev,
      [moduleId]: {
        ...(prev[moduleId] || {}),
        status: 'completed',
        startedAt: prev[moduleId]?.startedAt || new Date().toISOString(),
        completedAt: new Date().toISOString(),
      },
    }));
  }, []);

  /**
   * Save assessment results
   */
  const saveAssessmentResults = useCallback((results) => {
    setAssessmentResults(results);
    setHasCompletedAssessment(true);
    setAssessmentCount(prev => prev + 1);
  }, []);

  /**
   * Mark a toolkit tool as used
   */
  const markToolUsed = useCallback((toolId) => {
    setToolkitUsage(prev => {
      if (prev.includes(toolId)) return prev;
      return [...prev, toolId];
    });
  }, []);

  /**
   * Mark the scene setter as seen
   */
  const markSceneSetterSeen = useCallback(() => {
    setHasSeenSceneSetter(true);
  }, []);

  /**
   * Mark the resources library as visited
   */
  const markResourcesVisited = useCallback(() => {
    setResourcesVisited(true);
  }, []);

  /**
   * Snapshot of progress state in the shape the achievement predicates
   * expect (see data/achievements.js). Callers may pass overrides for
   * facts that React state hasn't committed yet (e.g. the module or tool
   * completed a moment ago).
   */
  const getAchievementContext = useCallback((overrides = {}) => {
    const completedIds = Object.entries(moduleProgress)
      .filter(([, p]) => p?.status === 'completed')
      .map(([id]) => parseInt(id, 10))
      .filter(Number.isFinite);
    return {
      modulesCompleted: completedIds.length,
      completedIds,
      totalModules: 8,
      assessmentDone: hasCompletedAssessment,
      assessmentCount,
      toolsUsed: toolkitUsage.length,
      hasSeenSceneSetter,
      resourcesVisited,
      ...overrides,
    };
  }, [moduleProgress, hasCompletedAssessment, assessmentCount, toolkitUsage, hasSeenSceneSetter, resourcesVisited]);

  const value = useMemo(() => ({
    // State
    moduleProgress,
    assessmentResults,
    assessmentCount,
    toolkitUsage,
    hasSeenSceneSetter,
    hasCompletedAssessment,
    resourcesVisited,

    // Methods
    markModuleStarted,
    markModuleCompleted,
    saveAssessmentResults,
    markToolUsed,
    markSceneSetterSeen,
    markResourcesVisited,
    getAchievementContext,
  }), [
    moduleProgress,
    assessmentResults,
    assessmentCount,
    toolkitUsage,
    hasSeenSceneSetter,
    hasCompletedAssessment,
    resourcesVisited,
    markModuleStarted,
    markModuleCompleted,
    saveAssessmentResults,
    markToolUsed,
    markSceneSetterSeen,
    markResourcesVisited,
    getAchievementContext,
  ]);

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export default ProgressContext;
